import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types';
import { useUnreadMessages } from '../../contexts/UnreadMessagesContext';

export const TopTabsNav: React.FC = () => {
    const { user } = useAuth();
    const { totalUnreadCount } = useUnreadMessages();
    const isDomina = user?.role === UserRole.DOMINA;
    const [activeCategory, setActiveCategory] = useState<'all' | 'uebersicht' | 'klienten' | 'aufgaben' | 'interaktion' | 'verwaltung'>('all');

    const dominaSections = [
        {
            category: 'uebersicht',
            label: 'Übersicht',
            items: [
                { to: '/', icon: '🏠', label: 'Dashboard' },
            ]
        },
        {
            category: 'klienten',
            label: 'Klienten',
            items: [
                { to: '/clients', icon: '👥', label: 'Klientenübersicht' },
            ]
        },
        {
            category: 'aufgaben',
            label: 'Aufgaben & Planung',
            items: [
                { to: '/coaching', icon: '🧭', label: 'Coaching' },
                { to: '/tasks', icon: '✅', label: 'Aufgaben' },
                { to: '/structure', icon: '⏳', label: 'Struktur' },
                { to: '/calendar', icon: '🗓️', label: 'Kalender' },
                { to: '/timer', icon: '⏱️', label: 'Timer & Wecker' },
                { to: '/control', icon: '🧠', label: 'Zielmatrix' },
            ]
        },
        {
            category: 'interaktion',
            label: 'Interaktion',
            items: [
                { to: '/messenger', icon: '💬', label: 'Messenger', badgeCount: totalUnreadCount },
                { to: '/audio-inputs', icon: '🎧', label: 'Flüsterraum' },
                { to: '/diary', icon: '📖', label: 'Tagebuch' },
                { to: '/reports', icon: '📜', label: 'Rapporte' },
            ]
        },
        {
            category: 'verwaltung',
            label: 'Verwaltung',
            items: [
                { to: '/payments', icon: '💰', label: 'Zahlungen' },
                { to: '/rules', icon: '👑', label: 'Regelwerke' },
                { to: '/media', icon: '🗂️', label: 'Mediathek' },
                { to: '/settings', icon: '⚙️', label: 'Einstellungen' },
            ]
        }
    ];

    const subItems = [
        { to: '/', icon: '🏠', label: 'Dashboard' },
        { to: '/coaching', icon: '🧭', label: 'Coaching' },
        { to: '/tasks', icon: '✅', label: 'Aufgaben' },
        { to: '/planning', icon: '⏳', label: 'Struktur' },
        { to: '/calendar', icon: '🗓️', label: 'Kalender' },
        { to: '/timer', icon: '⏱️', label: 'Timer' },
        { to: '/messenger', icon: '💬', label: 'Messenger', badgeCount: totalUnreadCount },
        { to: '/audio-inputs', icon: '🎧', label: 'Flüsterraum' },
        { to: '/diary', icon: '📖', label: 'Tagebuch' },
        { to: '/reports', icon: '📜', label: 'Rapport' },
        { to: '/rules', icon: '👑', label: 'Regeln' },
        { to: '/media', icon: '🗂️', label: 'Mediathek' },
        { to: '/profile', icon: '👤', label: 'Profil' },
        { to: '/settings', icon: '⚙️', label: 'Einstellungen' },
    ];

    const visibleItems = isDomina
        ? (activeCategory === 'all'
            ? dominaSections.flatMap(s => s.items)
            : dominaSections.find(s => s.category === activeCategory)?.items || [])
        : subItems;

    return (
        <div className="w-full bg-surface/95 backdrop-blur-xl border-b border-border/40 sticky top-16 z-20 shadow-md">
            <div className="max-w-7xl mx-auto px-4">
                {/* Category selector for Domina */}
                {isDomina && (
                    <div className="flex items-center gap-1.5 py-2 overflow-x-auto scrollbar-none border-b border-border/30 text-xs">
                        <button
                            onClick={() => setActiveCategory('all')}
                            className={`px-3 py-1.5 rounded-full font-bold whitespace-nowrap transition-all cursor-pointer ${
                                activeCategory === 'all' 
                                    ? 'bg-primary text-white shadow-glow' 
                                    : 'bg-surface-light text-text-secondary hover:text-text-main'
                            }`}
                        >
                            Alle Bereiche
                        </button>
                        {dominaSections.map(sec => (
                            <button
                                key={sec.category}
                                onClick={() => setActiveCategory(sec.category as 'uebersicht' | 'klienten' | 'aufgaben' | 'interaktion' | 'verwaltung')}
                                className={`px-3 py-1.5 rounded-full font-bold whitespace-nowrap transition-all cursor-pointer ${
                                    activeCategory === sec.category 
                                        ? 'bg-primary text-white shadow-glow' 
                                        : 'bg-surface-light text-text-secondary hover:text-text-main'
                                }`}
                            >
                                {sec.label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Sub items bar */}
                <div className="flex items-center gap-2 py-2 overflow-x-auto scrollbar-none">
                    {visibleItems.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) =>
                                `flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${
                                    isActive
                                        ? 'bg-primary/20 border-primary text-text-main shadow-glow shadow-primary/10'
                                        : 'bg-surface-light/40 border-border/30 hover:bg-surface-light text-text-secondary hover:text-text-main'
                                }`
                            }
                        >
                            <span className="text-base">{item.icon}</span>
                            <span>{item.label}</span>
                            {item.badgeCount !== undefined && item.badgeCount > 0 && (
                                <span className="bg-rose-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.2 animate-pulse">
                                    {item.badgeCount}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </div>
            </div>
        </div>
    );
};
